import * as XLSX from 'xlsx';
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { handleLogout, microsoftAuthService, msalInstance } from "./outlook/config";
import { handleGoogleLogin, handleGoogleLogout, initGoogleClient } from "./google/config";
import { AccountSelector, FilePreviewPopup, LoadingScreen, NoAuthScreen, ProfileHeader } from "./widgets";
import { createOneDriveFile, downloadOneDriveFile, fetchOneDriveFiles, searchOneDriveFiles } from "./outlook/onedrive";
import { createGoogleDriveFile, downloadGoogleDriveFile, fetchGoogleDriveFiles, searchGoogleDriveFiles } from "./google/drive";
import { dark_mode_primary, dark_mode_secondary, AppMode, oAuthStatus, AuthProvider, PaymentTiers, light_mode_secondary, light_mode_primary } from "./values";

export function Files({ appMode, setAuthStatus, user, setUser, accounts, setAccounts, loading, setLoading, error, setError, isGoogleLoggedIn, isMicrosoftLoggedIn, setIsGoogleLoggedIn, setIsMicrosoftLoggedIn, authManager, providerAccounts, setActiveTab, toggleAppMode, subscriptionTier }) {
    const history = useHistory();
    const initializedRef = useRef(false);
    const [fileList, setFileList] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [accessToken, setAccessToken] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [updateTrigger, setUpdateTrigger] = useState(0);
    const [activeAccount, setActiveAccount] = useState(null);
    const [fetchingFiles, setFetchingFiles] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
    const [showAccountSelector, setShowAccountSelector] = useState(false);

    useEffect(() => {
        const handleAuthStateChange = () => {
            if (authManager) {
                setAccounts(authManager.getLoggedInAccounts());
                setUpdateTrigger(prev => prev + 1);
            }
        };
        window.addEventListener('authStateChanged', handleAuthStateChange);
        authManager.addStateChangeListener(handleAuthStateChange);
        return () => {
            window.removeEventListener('authStateChanged', handleAuthStateChange);
            authManager.removeStateChangeListener(handleAuthStateChange);
        };//eslint-disable-next-line
    }, [setAccounts]);

    useEffect(() => {
        if (authManager) {
            setAccounts(authManager.getLoggedInAccounts());
        }//eslint-disable-next-line
    }, [updateTrigger, setAccounts]);

    useEffect(() => {
        if (subscriptionTier !== undefined) {
            const accountsCount = authManager ? authManager.getLoggedInAccounts().length : 0;
            setSubscriptionInfo({
                tier: subscriptionTier,
                name: subscriptionTier === 0
                    ? 'Free'
                    : subscriptionTier === 1
                        ? 'Starter'
                        : subscriptionTier === 2
                            ? 'Pro'
                            : 'Enterprise',
                accountLimit: subscriptionTier === 0
                    ? 2
                    : subscriptionTier === 1
                        ? 5
                        : subscriptionTier === 2
                            ? 10
                            : 15,
                accountsUsed: accountsCount,
                canAddMore: accountsCount < (subscriptionTier === 0
                    ? 2
                    : subscriptionTier === 1
                        ? 5
                        : subscriptionTier === 2
                            ? 10
                            : 15
                ),
            });
        } else if (authManager) {
            try {
                if (typeof authManager.getSubscriptionInfo === 'function') {
                    setSubscriptionInfo(authManager.getSubscriptionInfo());
                } else {
                    const accountsCount = authManager.getLoggedInAccounts().length;
                    setSubscriptionInfo({
                        tier: PaymentTiers.FREE,
                        name: 'Free',
                        accountLimit: 2,
                        accountsUsed: accountsCount,
                        canAddMore: accountsCount < 2,
                    });
                }
            } catch (error) {
                setSubscriptionInfo({
                    tier: PaymentTiers.FREE,
                    name: 'Free',
                    accountLimit: 2,
                    accountsUsed: 0,
                    canAddMore: true,
                });
            }
        }//eslint-disable-next-line
    }, [updateTrigger, subscriptionTier]);

    useEffect(() => {
        if (!activeAccount && providerAccounts) {
            const googleActive = providerAccounts[AuthProvider.GOOGLE];
            const microsoftActive = providerAccounts[AuthProvider.MICROSOFT];
            if (googleActive) {
                setActiveAccount(googleActive);
                setSelectedProvider(AuthProvider.GOOGLE);
                if (!fetchingFiles) {
                    fetchFiles(AuthProvider.GOOGLE, googleActive);
                }
            } else if (microsoftActive) {
                setActiveAccount(microsoftActive);
                setSelectedProvider(AuthProvider.MICROSOFT);
                if (!fetchingFiles) {
                    fetchFiles(AuthProvider.MICROSOFT, microsoftActive);
                }
            }
        }//eslint-disable-next-line
    }, [providerAccounts, activeAccount, fetchingFiles]);

    const handleSearch = async (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (!query.trim()) {
            await fetchFiles(selectedProvider);
            return;
        }
        if (query.length < 3) return;
        setIsSearching(true);
        try {
            if (selectedProvider === AuthProvider.GOOGLE) {
                await searchGoogleDriveFiles(query, setIsSearching, activeAccount, setFileList);
            } else if (selectedProvider === AuthProvider.MICROSOFT) {
                await searchOneDriveFiles(query, setIsSearching, activeAccount, setFileList);
            }
        } catch (error) {
            setError(`Search failed: ${error.message}`);
            setShowErrorPopup(true);
        } finally {
            setIsSearching(false);
        }
    };

    const canAddMoreAccounts = useCallback(() => {
        if (!authManager) return true;
        if (subscriptionTier !== undefined) {
            const accountLimit = subscriptionTier === 0 ? 2 :
                subscriptionTier === 1 ? 5 :
                    subscriptionTier === 2 ? 10 : 15;
            return authManager.getLoggedInAccounts().length < accountLimit;
        }
        try {
            if (typeof authManager.canAddMoreAccounts === 'function') {
                return authManager.canAddMoreAccounts();
            } else {
                return authManager.getLoggedInAccounts().length < 2;
            }
        } catch (error) {
            return false;
        }//eslint-disable-next-line
    }, [subscriptionTier]);

    const handlePreviewFile = async (file) => {
        try {
            setLoading(true);
            let token = null;
            if (file.provider === AuthProvider.MICROSOFT) {
                try {
                    if (typeof activeAccount === 'string') {
                        token = await microsoftAuthService.getAccessToken(activeAccount, ['Files.Read'], false);
                    } else if (activeAccount?.id) {
                        token = await microsoftAuthService.getAccessToken(activeAccount.id, ['Files.Read'], false);
                    } else {
                        const tokenResponse = await microsoftAuthService.msalInstance.acquireTokenSilent({
                            scopes: ['Files.Read'],
                            account: activeAccount,
                        });
                        token = tokenResponse.accessToken;
                    }
                } catch (error) {
                    if (error.name === "InteractionRequiredAuthError") {
                        const tokenResponse = await microsoftAuthService.msalInstance.acquireTokenPopup({
                            scopes: ['Files.Read'],
                            account: activeAccount,
                        });
                        token = tokenResponse.accessToken;
                    } else {
                        throw error;
                    }
                }
            } else if (file.provider === AuthProvider.GOOGLE) {
                token = window.gapi?.auth?.getToken()?.access_token || null;
            }
            setAccessToken(token);
            setPreviewFile(file);
            setIsPreviewOpen(true);
        } catch (error) {
            setError(`Failed to prepare file preview: ${error.message}`);
            setShowErrorPopup(true);
        } finally {
            setLoading(false);
        }
    };

    const handleClosePreview = () => {
        setIsPreviewOpen(false);
        setPreviewFile(null);
    };

    const handleUpgradeRedirect = () => {
        setShowUpgradePrompt(false);
        history.push('/pricing');
    };

    const handleAccountSelect = async (account) => {
        try {
            if (!account || !account.provider) {
                throw new Error("Invalid account selected");
            }
            setLoading(true);
            setActiveAccount(account);
            const userData = account.userData || account;
            setUser(userData);
            setSelectedProvider(account.provider);
            if (authManager) {
                const accountId = account.id || `${account.provider}-${account.email || account.username}`;
                const storedAccount = authManager.getAccounts().find(acc => acc.id === accountId);
                if (storedAccount) {
                    authManager.setActiveAccount(storedAccount.id);
                }
            }
            await fetchFiles(account.provider, account);
            setShowAccountSelector(false);
        } catch (err) {
            setError(`Failed to load files: ${err.message}`);
            setShowErrorPopup(true);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveAccount = async (account) => {
        try {
            setLoading(true);
            if (!authManager) {
                throw new Error("Auth manager is required");
            }
            const accountId = account.id || `${account.provider}-${account.email || account.username}`;
            const storedAccount = authManager.getAccounts().find(acc => acc.id === accountId);

            if (!storedAccount) {
                throw new Error("Account not found");
            }
            let result = false;
            if (account.provider === AuthProvider.MICROSOFT) {
                result = await handleLogout(accountId, setUser, setLoading, setError, setIsMicrosoftLoggedIn);
            } else if (account.provider === AuthProvider.GOOGLE) {
                result = await handleGoogleLogout(authManager, accountId, setUser, setLoading, setError, setIsGoogleLoggedIn);
            }
            if (result) {
                if (authManager.getAccounts().some(acc => acc.id === accountId)) {
                    authManager.removeAccount(accountId);
                }
                try {
                    const savedAccounts = localStorage.getItem('AuthAccounts');
                    if (savedAccounts) {
                        const parsedAccounts = JSON.parse(savedAccounts);
                        const updatedAccounts = parsedAccounts.filter(acc => acc.id !== accountId);
                        if (parsedAccounts.length !== updatedAccounts.length) {
                            localStorage.setItem('AuthAccounts', JSON.stringify(updatedAccounts));
                        }
                    }
                    const activeAccounts = localStorage.getItem('ActiveAuthAccounts');
                    if (activeAccounts) {
                        const parsedActiveAccounts = JSON.parse(activeAccounts);
                        if (parsedActiveAccounts[account.provider] === accountId) {
                            const remainingAccounts = authManager.getAccounts().filter(
                                acc => acc.provider === account.provider && acc.id !== accountId
                            );
                            if (remainingAccounts.length > 0) {
                                parsedActiveAccounts[account.provider] = remainingAccounts[0].id;
                            } else {
                                delete parsedActiveAccounts[account.provider];
                            }
                            localStorage.setItem('ActiveAuthAccounts', JSON.stringify(parsedActiveAccounts));
                        }
                    }
                    const tokens = localStorage.getItem('AuthTokens');
                    if (tokens) {
                        const parsedTokens = JSON.parse(tokens);
                        if (parsedTokens[accountId]) {
                            delete parsedTokens[accountId];
                            localStorage.setItem('AuthTokens', JSON.stringify(parsedTokens));
                        }
                    }
                } catch (storageError) { }
                if (activeAccount &&
                    ((activeAccount.username === account.username && activeAccount.provider === account.provider) ||
                        (activeAccount.userData && activeAccount.userData.username === account.username && activeAccount.userData.provider === account.provider))) {

                    const remainingAccounts = authManager.getLoggedInAccounts();
                    if (remainingAccounts.length > 0) {
                        setTimeout(() => {
                            handleAccountSelect(remainingAccounts[0]);
                        }, 0);
                    } else {
                        setActiveAccount(null);
                        setFileList([]);
                        setSelectedProvider(null);
                    }
                }

                const remainingProviderAccounts = authManager.getAccounts(account.provider);
                if (remainingProviderAccounts.length === 0) {
                    if (account.provider === AuthProvider.GOOGLE) {
                        setIsGoogleLoggedIn(oAuthStatus.NOT_LOGGED_IN);
                    } else if (account.provider === AuthProvider.MICROSOFT) {
                        setIsMicrosoftLoggedIn(oAuthStatus.NOT_LOGGED_IN);
                    }
                }
                authManager.notifyStateChange();
                setUpdateTrigger(prev => prev + 1);
                setAccounts(authManager.getLoggedInAccounts());
            }
        } catch (error) {
            setError(`Failed to remove account: ${error.message}`);
            setShowErrorPopup(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const initializeAuth = async () => {
            if (initializedRef.current) return;
            try {
                setLoading(true);
                initializedRef.current = true;
                if (isMicrosoftLoggedIn === oAuthStatus.LOGGED_IN) {
                    try {
                        await microsoftAuthService.initialize();
                        const result = await msalInstance.handleRedirectPromise();
                        if (result) {
                            const newAccount = {
                                ...result.account,
                                provider: AuthProvider.MICROSOFT,
                                email: result.account.username,
                                username: result.account.username,
                            };
                            setUser(newAccount);
                            if (authManager) {
                                try {
                                    if (!canAddMoreAccounts()) {
                                        setShowUpgradePrompt(true);
                                        return;
                                    }
                                    const existingAccounts = authManager.getAccounts(AuthProvider.MICROSOFT);
                                    const existingAccount = existingAccounts.find(acc =>
                                        acc.userData && acc.userData.username === newAccount.username
                                    );
                                    let accountId;
                                    if (existingAccount) {
                                        authManager.updateUserData(existingAccount.id, newAccount);
                                        accountId = existingAccount.id;
                                    } else {
                                        const accountRecord = authManager.addAccount(AuthProvider.MICROSOFT, newAccount);
                                        accountId = accountRecord.id;
                                    }
                                    authManager.setActiveAccount(accountId);
                                    authManager.notifyStateChange();
                                    setUpdateTrigger(prev => prev + 1);
                                    setActiveAccount(newAccount);
                                    setSelectedProvider(AuthProvider.MICROSOFT);
                                    await fetchOneDriveFiles(newAccount, setLoading, setError).then(files => setFileList(files || []));
                                } catch (error) {
                                    if (error.message && error.message.includes("Account limit reached")) {
                                        setShowUpgradePrompt(true);
                                        return;
                                    }
                                    throw error;
                                }
                            } else {
                                setActiveAccount(newAccount);
                                setSelectedProvider(AuthProvider.MICROSOFT);
                                await fetchOneDriveFiles(newAccount, setLoading, setError).then(files => setFileList(files || []));
                            }
                        } else {
                            const msAccounts = msalInstance.getAllAccounts();
                            if (msAccounts.length > 0) {
                                if (authManager) {
                                    const storedMsAccounts = authManager.getAccounts(AuthProvider.MICROSOFT);
                                    if (storedMsAccounts.length === 0) {
                                        const primaryAccount = {
                                            ...msAccounts[0],
                                            provider: AuthProvider.MICROSOFT,
                                            email: msAccounts[0].username,
                                            username: msAccounts[0].username,
                                        };
                                        setUser(primaryAccount);
                                        try {
                                            if (!canAddMoreAccounts()) {
                                                setShowUpgradePrompt(true);
                                                return;
                                            }
                                            const accountRecord = authManager.addAccount(AuthProvider.MICROSOFT, primaryAccount);
                                            authManager.setActiveAccount(accountRecord.id);
                                            authManager.notifyStateChange();
                                            setUpdateTrigger(prev => prev + 1);
                                            setActiveAccount(primaryAccount);
                                            setSelectedProvider(AuthProvider.MICROSOFT);
                                            await fetchOneDriveFiles(primaryAccount, setLoading, setError).then(files => setFileList(files || []));
                                        } catch (error) {
                                            if (error.message && error.message.includes("Account limit reached")) {
                                                setShowUpgradePrompt(true);
                                                return;
                                            }
                                            throw error;
                                        }
                                    } else {
                                        const activeAccount = authManager.getActiveAccount(AuthProvider.MICROSOFT);
                                        if (activeAccount) {
                                            setUser(activeAccount.userData);
                                            setActiveAccount(activeAccount.userData);
                                            setSelectedProvider(AuthProvider.MICROSOFT);
                                            await fetchOneDriveFiles(activeAccount.userData, setLoading, setError).then(files => setFileList(files || []));
                                        } else if (storedMsAccounts.length > 0) {
                                            setUser(storedMsAccounts[0].userData);
                                            setActiveAccount(storedMsAccounts[0].userData);
                                            setSelectedProvider(AuthProvider.MICROSOFT);
                                            authManager.setActiveAccount(storedMsAccounts[0].id);
                                            await fetchOneDriveFiles(storedMsAccounts[0].userData, setLoading, setError).then(files => setFileList(files || []));
                                        }
                                    }
                                } else {
                                    msalInstance.setActiveAccount(msAccounts[0]);
                                    setActiveAccount(msAccounts[0]);
                                    setSelectedProvider(AuthProvider.MICROSOFT);
                                    await fetchOneDriveFiles(msAccounts[0], setLoading, setError).then(files => setFileList(files || []));
                                }
                            }
                        }
                    } catch (error) {
                        setError(`Microsoft authentication error: ${error.message}`);
                        setShowErrorPopup(true);
                    }
                }

                if (isGoogleLoggedIn === oAuthStatus.LOGGED_IN && (!activeAccount || activeAccount.provider !== AuthProvider.MICROSOFT)) {
                    try {
                        await initGoogleClient();
                        await new Promise(resolve => setTimeout(resolve, 500));
                        if (!window.gapi || !window.gapi.auth2) {
                            throw new Error("Google Auth not initialized properly");
                        }

                        const authInstance = window.gapi.auth2.getAuthInstance();
                        if (!authInstance) {
                            throw new Error("Failed to get Google Auth instance");
                        }

                        const googleUser = authInstance.currentUser.get();
                        if (googleUser && googleUser.isSignedIn()) {
                            const profile = googleUser.getBasicProfile();
                            const googleAccount = {
                                provider: AuthProvider.GOOGLE,
                                id: profile.getId(),
                                name: profile.getName(),
                                email: profile.getEmail(),
                                username: profile.getEmail(),
                                photoURL: profile.getImageUrl(),
                            };
                            setUser(googleAccount);
                            if (authManager) {
                                const storedGoogleAccounts = authManager.getAccounts(AuthProvider.GOOGLE);
                                let accountExists = false;
                                let existingAccount = null;
                                for (const acc of storedGoogleAccounts) {
                                    if (acc.userData && acc.userData.email === googleAccount.email) {
                                        accountExists = true;
                                        existingAccount = acc;
                                        break;
                                    }
                                }
                                if (!accountExists) {
                                    try {
                                        if (!canAddMoreAccounts()) {
                                            setShowUpgradePrompt(true);
                                            return;
                                        }
                                        const accountRecord = authManager.addAccount(AuthProvider.GOOGLE, googleAccount);
                                        authManager.setActiveAccount(accountRecord.id);
                                        authManager.notifyStateChange();
                                        setUpdateTrigger(prev => prev + 1);
                                        if (!activeAccount || activeAccount.provider !== AuthProvider.MICROSOFT) {
                                            setActiveAccount(googleAccount);
                                            setSelectedProvider(AuthProvider.GOOGLE);
                                            await fetchGoogleDriveFiles(googleAccount, setLoading, setError).then(files => setFileList(files || []));
                                        }
                                    } catch (error) {
                                        if (error.message && error.message.includes("Account limit reached")) {
                                            setShowUpgradePrompt(true);
                                            return;
                                        }
                                        throw error;
                                    }
                                } else if (existingAccount) {
                                    authManager.updateUserData(existingAccount.id, googleAccount);
                                    if (!activeAccount || activeAccount.provider !== AuthProvider.MICROSOFT) {
                                        authManager.setActiveAccount(existingAccount.id);
                                        setActiveAccount(googleAccount);
                                        setSelectedProvider(AuthProvider.GOOGLE);
                                        await fetchGoogleDriveFiles(googleAccount, setLoading, setError).then(files => setFileList(files || []));
                                    }
                                }
                            } else {
                                if (!activeAccount || activeAccount.provider !== AuthProvider.MICROSOFT) {
                                    setActiveAccount(googleAccount);
                                    setSelectedProvider(AuthProvider.GOOGLE);
                                    await fetchGoogleDriveFiles(googleAccount, setLoading, setError).then(files => setFileList(files || []));
                                }
                            }
                        } else {
                            if (authManager && (!activeAccount || activeAccount.provider !== AuthProvider.MICROSOFT)) {
                                try {
                                    if (!canAddMoreAccounts()) {
                                        setShowUpgradePrompt(true);
                                        return;
                                    }
                                    await handleGoogleLogin(authManager, setUser, setLoading, setError, setIsGoogleLoggedIn);
                                } catch (error) {
                                    if (error.message && error.message.includes("Account limit reached")) {
                                        setShowUpgradePrompt(true);
                                        return;
                                    }
                                    throw error;
                                }
                            }
                        }
                    } catch (error) {
                        if (error.message && error.message.includes("Account limit reached")) {
                            setShowUpgradePrompt(true);
                        } else {
                            setError(`Google account handling failed: ${error.message}`);
                            setShowErrorPopup(true);
                            setIsGoogleLoggedIn(oAuthStatus.NOT_LOGGED_IN);
                        }
                    }
                }
            } catch (err) {
                setError(`Authentication initialization failed: ${err.message}`);
                setShowErrorPopup(true);
            } finally {
                setIsInitialized(true);
                setLoading(false);
            }
        };
        if (!isInitialized && !initializedRef.current) {
            initializeAuth();
        }
    }, [isGoogleLoggedIn, setIsGoogleLoggedIn, isMicrosoftLoggedIn, setIsMicrosoftLoggedIn, setError, setLoading, setUser, authManager, activeAccount, canAddMoreAccounts, isInitialized, selectedProvider]);

    useEffect(() => {
        if ((!activeAccount || !accounts.some(acc => acc.id === activeAccount.id)) && accounts.length > 0) {
            let preferred = null;
            if (selectedProvider) {
                preferred = accounts.find(acc => acc.provider === selectedProvider);
            }
            setActiveAccount(preferred || accounts[0]);
        }
    }, [accounts, activeAccount, selectedProvider]);

    const FeatureButton = ({ icon, title, onClick, isActive = false }) => {
        return (
            <div className="px-2 w-1/4">
                <button className={`w-full ${isActive ? 'bg-blue-50 text-blue-700 border-blue-200' : ''} ${appMode === AppMode.LIGHT ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-100'} h-full border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} rounded-lg flex items-center justify-center py-2.5 px-4 ${appMode === AppMode.LIGHT ? 'hover:bg-gray-50' : 'hover:bg-gray-800'} transition-colors duration-200 shadow-sm hover:shadow`} onClick={onClick}>
                    <i className={`${icon} mr-2 ${isActive ? 'text-blue-600' : appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}></i>
                    <span className="font-medium">{title}</span>
                </button>
            </div>
        );
    };

    const NewDocButton = ({ title, icon, acceptedFormats }) => {
        const showEdRef = useRef(null);
        const editorRef = useRef(null);
        const [showPopup, setShowPopup] = useState(false);
        const [documentName, setDocumentName] = useState('');
        const [editorContent, setEditorContent] = useState('');//eslint-disable-next-line
        const [selectedFile, setSelectedFile] = useState(null);
        const [isCreatingNew, setIsCreatingNew] = useState(false);

        const [excelData, setExcelData] = useState([
            [{ value: '' }, { value: '' }, { value: '' }],
            [{ value: '' }, { value: '' }, { value: '' }],
            [{ value: '' }, { value: '' }, { value: '' }]
        ]);
        const [activeSheet, setActiveSheet] = useState(0);
        const [sheets, setSheets] = useState([{ name: 'Sheet1', data: [] }]);
        const [slides, setSlides] = useState([
            { title: 'Title Slide', content: '', background: null, layout: 'title' }
        ]);
        const [currentSlide, setCurrentSlide] = useState(0);//eslint-disable-next-line
        const [slideLayout, setSlideLayout] = useState('title');

        useEffect(() => {
            const handleClickOutside = (event) => {
                if (showEdRef.current && !showEdRef.current.contains(event.target)) {
                    setShowPopup(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, []);

        useEffect(() => {
            if (isCreatingNew && showPopup && title.includes('Word')) {
                if (window.Quill && editorRef.current) {
                    const quill = new window.Quill(editorRef.current, {
                        modules: {
                            toolbar: [
                                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'color': [] }, { 'background': [] }],
                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                [{ 'align': [] }],
                                ['link', 'image'],
                                ['clean'],
                            ],
                        },
                        theme: 'snow',
                    });
                    quill.on('text-change', () => {
                        setEditorContent(quill.root.innerHTML);
                    });
                }
            }
        }, [isCreatingNew, showPopup, title]);

        const handleButtonClick = () => {
            if (!selectedProvider) {
                setError("Please select a storage provider first");
                setShowErrorPopup(true);
                return;
            }
            setIsCreatingNew(true);
            setEditorContent('');
            setDocumentName(`New ${title.replace('New ', '')}`);
            if (title.includes('Excel')) {
                setExcelData([
                    [{ value: '' }, { value: '' }, { value: '' }],
                    [{ value: '' }, { value: '' }, { value: '' }],
                    [{ value: '' }, { value: '' }, { value: '' }]
                ]);
                setSheets([{ name: 'Sheet1', data: [] }]);
                setActiveSheet(0);
            } else if (title.includes('PPT')) {
                setSlides([{ title: 'Title Slide', content: '', background: null, layout: 'title' }]);
                setCurrentSlide(0);
                setSlideLayout('title');
            }

            setShowPopup(true);
            if (title.includes('Word') && !window.Quill) {
                const script = document.createElement('script');
                script.src = 'https://cdn.quilljs.com/1.3.6/quill.min.js';
                script.async = true;
                script.onload = () => {
                    setShowPopup(false);
                    setTimeout(() => setShowPopup(true), 10);
                };
                document.body.appendChild(script);
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = 'https://cdn.quilljs.com/1.3.6/quill.snow.css';
                document.head.appendChild(link);
            }
        };

        const handleFileChange = (event) => {
            const file = event.target.files[0];
            if (!file) return;
            setSelectedFile(file);
            setShowPopup(false);
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    setLoading(true);
                    await createFile(selectedProvider, file.name, e.target.result, file.type);
                    await fetchFiles(selectedProvider);
                } catch (error) {
                    setError(`Error uploading file: ${error.message}`);
                    setShowErrorPopup(true);
                } finally {
                    setLoading(false);
                }
            };
            reader.readAsArrayBuffer(file);
        };

        const handleExcelCellChange = (row, col, value) => {
            const newData = [...excelData];
            newData[row][col] = { value };
            setExcelData(newData);
        };

        const addExcelRow = () => {
            const newRow = Array(excelData[0].length).fill().map(() => ({ value: '' }));
            setExcelData([...excelData, newRow]);
        };

        const addExcelColumn = () => {
            const newData = excelData.map(row => [...row, { value: '' }]);
            setExcelData(newData);
        };

        const addExcelSheet = () => {
            const sheetName = `Sheet${sheets.length + 1}`;
            setSheets([...sheets, { name: sheetName, data: [] }]);
            setActiveSheet(sheets.length);
        };

        const addSlide = () => {
            setSlides([...slides, {
                title: `Slide ${slides.length + 1}`,
                content: '',
                background: null,
                layout: 'content'
            }]);
            setCurrentSlide(slides.length);
        };

        const updateSlideContent = (index, field, value) => {
            const updatedSlides = [...slides];
            updatedSlides[index] = { ...updatedSlides[index], [field]: value };
            setSlides(updatedSlides);
        };

        const changeSlideLayout = (layout) => {
            setSlideLayout(layout);
            updateSlideContent(currentSlide, 'layout', layout);
        };

        const handleCreateDocument = async () => {
            if (!documentName) {
                setError("Please enter a document name");
                setShowErrorPopup(true);
                return;
            }
            try {
                setLoading(true);
                let mimeType, fileContent, fileName;
                if (title.includes('Word')) {
                    mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                    fileName = documentName.endsWith('.docx') ? documentName : `${documentName}.docx`;
                    fileContent = editorContent;
                } else if (title.includes('Excel')) {
                    mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                    fileName = documentName.endsWith('.xlsx') ? documentName : `${documentName}.xlsx`;
                    const workbook = {
                        SheetNames: sheets.map(sheet => sheet.name),
                        Sheets: {},
                    };
                    sheets.forEach((sheet, sheetIndex) => {
                        const wsData = sheetIndex === activeSheet ? excelData : sheet.data;
                        const worksheet = {};
                        wsData.forEach((row, rowIndex) => {
                            row.forEach((cell, colIndex) => {
                                if (cell.value !== '') {
                                    const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
                                    worksheet[cellRef] = { v: cell.value };
                                }
                            });
                        });
                        if (wsData.length > 0) {
                            const range = { s: { c: 0, r: 0 }, e: { c: wsData[0].length - 1, r: wsData.length - 1 } };
                            worksheet['!ref'] = XLSX.utils.encode_range(range);
                        }
                        workbook.Sheets[sheet.name] = worksheet;
                    });
                    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary' });
                    const buf = new ArrayBuffer(wbout.length);
                    const view = new Uint8Array(buf);
                    for (let i = 0; i < wbout.length; i++) {
                        view[i] = wbout.charCodeAt(i) & 0xFF;
                    }
                    fileContent = buf;
                } else if (title.includes('PPT')) {
                    mimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
                    fileName = documentName.endsWith('.pptx') ? documentName : `${documentName}.pptx`;
                    const pptx = new window.PptxGenJS();
                    slides.forEach(slide => {
                        const pptxSlide = pptx.addSlide();
                        if (slide.title) {
                            pptxSlide.addText(slide.title, {
                                x: 1,
                                y: 0.5,
                                fontSize: 24,
                                bold: true,
                                color: '363636',
                            });
                        }
                        if (slide.layout === 'title') {
                            pptxSlide.addText('Click to add subtitle', {
                                x: 1,
                                y: 2,
                                fontSize: 16,
                                color: '666666',
                            });
                        } else if (slide.layout === 'content') {
                            pptxSlide.addText(slide.content || 'Click to add content', {
                                x: 1,
                                y: 2,
                                w: 8,
                                h: 4,
                                fontSize: 14,
                                color: '363636',
                            });
                        } else if (slide.layout === 'two-content') {
                            pptxSlide.addText('Left content', {
                                x: 0.5,
                                y: 2,
                                w: 4,
                                h: 4,
                                fontSize: 14,
                                color: '363636',
                            });
                            pptxSlide.addText('Right content', {
                                x: 5.5,
                                y: 2,
                                w: 4,
                                h: 4,
                                fontSize: 14,
                                color: '363636',
                            });
                        }
                        if (slide.background) {
                            pptxSlide.background = { fill: slide.background };
                        }
                    });
                    const base64 = await pptx.writeFile({ outputType: 'base64' });
                    const binaryString = window.atob(base64);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    fileContent = bytes.buffer;
                }
                await createFile(selectedProvider, fileName, fileContent, mimeType);
                await fetchFiles(selectedProvider);
                setShowPopup(false);
            } catch (error) {
                setError(`Error creating document: ${error.message}`);
                setShowErrorPopup(true);
            } finally {
                setLoading(false);
            }
        };

        const handleClosePopup = () => {
            setShowPopup(false);
            setIsCreatingNew(false);
            setEditorContent('');
            setDocumentName('');
        };

        const renderExcelEditor = () => {
            return (
                <div className="mb-4">
                    <div className="flex mb-2 space-x-2">
                        <button onClick={addExcelRow} className={`px-2 py-1 rounded text-sm ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-700' : 'bg-blue-900 bg-opacity-30 text-blue-300'}`}>
                            <i className="bi bi-plus-lg mr-1"></i> Add Row
                        </button>
                        <button onClick={addExcelColumn} className={`px-2 py-1 rounded text-sm ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-700' : 'bg-blue-900 bg-opacity-30 text-blue-300'}`}>
                            <i className="bi bi-plus-lg mr-1"></i> Add Column
                        </button>
                        <button onClick={addExcelSheet} className={`px-2 py-1 rounded text-sm ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-700' : 'bg-blue-900 bg-opacity-30 text-blue-300'}`}>
                            <i className="bi bi-file-earmark-plus mr-1"></i> Add Sheet
                        </button>
                    </div>
                    <div className="flex mb-2 space-x-1 overflow-x-auto">
                        {sheets.map((sheet, index) => (
                            <button key={index} onClick={() => setActiveSheet(index)} className={`px-3 py-1 rounded-t text-sm ${activeSheet === index
                                ? appMode === AppMode.LIGHT
                                    ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-500'
                                    : 'bg-blue-900 bg-opacity-50 text-blue-200 border-b-2 border-blue-400'
                                : appMode === AppMode.LIGHT
                                    ? 'bg-gray-50 text-gray-700'
                                    : 'bg-gray-700 text-gray-300'
                                }`}
                            >
                                {sheet.name}
                            </button>
                        ))}
                    </div>
                    <div className={`border rounded-lg overflow-auto max-h-[400px] ${appMode === AppMode.LIGHT
                        ? 'border-gray-300 bg-white text-gray-700'
                        : 'border-gray-600 bg-gray-700 text-gray-300'
                        }`}>
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="w-10 p-1"></th>
                                    {excelData[0].map((_, colIndex) => <th key={colIndex} className={`p-1 text-center border ${appMode === AppMode.LIGHT ? 'border-gray-200 bg-gray-50' : 'border-gray-600 bg-gray-800'}`}>{String.fromCharCode(65 + colIndex)}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {excelData.map((row, rowIndex) => (
                                    <tr key={rowIndex}>
                                        <td className={`p-1 text-center border ${appMode === AppMode.LIGHT ? 'border-gray-200 bg-gray-50' : 'border-gray-600 bg-gray-800'}`}>{rowIndex + 1}</td>
                                        {row.map((cell, colIndex) => (
                                            <td key={colIndex} className={`p-0 border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-600'}`}>
                                                <input type="text" value={cell.value} onChange={(e) => handleExcelCellChange(rowIndex, colIndex, e.target.value)} className={`w-full p-1 outline-none ${appMode === AppMode.LIGHT
                                                    ? 'bg-white'
                                                    : 'bg-gray-700 text-gray-200'
                                                    }`}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        };

        const renderPowerPointEditor = () => {
            return (
                <div className="mb-4">
                    <div className="flex mb-4 space-x-2">
                        <button onClick={addSlide} className={`px-2 py-1 rounded text-sm ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-700' : 'bg-blue-900 bg-opacity-30 text-blue-300'}`}>
                            <i className="bi bi-plus-lg mr-1"></i> Add Slide
                        </button>
                        <div className="ml-4">
                            <select value={slides[currentSlide]?.layout || 'title'} onChange={(e) => changeSlideLayout(e.target.value)} className={`px-2 py-1 rounded text-sm ${appMode === AppMode.LIGHT
                                ? 'bg-white border border-gray-300 text-gray-700'
                                : 'bg-gray-700 border border-gray-600 text-gray-200'
                                }`}
                            >
                                <option value="title">Title Slide</option>
                                <option value="content">Content Slide</option>
                                <option value="two-content">Two Content</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex h-[400px]">
                        <div className={`w-1/4 overflow-auto border-r ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                            {slides.map((slide, index) => (
                                <div key={index} onClick={() => setCurrentSlide(index)} className={`p-2 mb-2 cursor-pointer ${currentSlide === index
                                    ? appMode === AppMode.LIGHT
                                        ? 'bg-blue-50 border-l-4 border-blue-500'
                                        : 'bg-blue-900 bg-opacity-20 border-l-4 border-blue-500'
                                    : ''
                                    }`}
                                >
                                    <div className={`aspect-video border flex items-center justify-center text-xs ${appMode === AppMode.LIGHT
                                        ? 'border-gray-300 bg-white'
                                        : 'border-gray-600 bg-gray-800'
                                        }`}>
                                        {slide.title || `Slide ${index + 1}`}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="w-3/4 p-4">
                            <div className={`aspect-video border mb-4 p-4 ${appMode === AppMode.LIGHT
                                ? 'border-gray-300 bg-white'
                                : 'border-gray-600 bg-gray-800'
                                }`}>
                                {slides[currentSlide]?.layout === 'title' ? (
                                    <div className="flex flex-col items-center justify-center h-full">
                                        <input type="text" value={slides[currentSlide]?.title || ''} onChange={(e) => updateSlideContent(currentSlide, 'title', e.target.value)} placeholder="Click to add title" className={`text-center text-2xl font-bold mb-4 w-full outline-none ${appMode === AppMode.LIGHT
                                            ? 'bg-white'
                                            : 'bg-gray-800 text-white'
                                            }`}
                                        />
                                        <input type="text" placeholder="Click to add subtitle" className={`text-center text-lg w-full outline-none ${appMode === AppMode.LIGHT
                                            ? 'bg-white text-gray-600'
                                            : 'bg-gray-800 text-gray-300'
                                            }`}
                                        />
                                    </div>
                                ) : slides[currentSlide]?.layout === 'two-content' ? (
                                    <div className="flex flex-col h-full">
                                        <input type="text" value={slides[currentSlide]?.title || ''} onChange={(e) => updateSlideContent(currentSlide, 'title', e.target.value)} placeholder="Click to add title" className={`text-xl font-bold mb-4 w-full outline-none ${appMode === AppMode.LIGHT
                                            ? 'bg-white'
                                            : 'bg-gray-800 text-white'
                                            }`}
                                        />
                                        <div className="flex flex-1">
                                            <textarea placeholder="Left content" className={`flex-1 p-2 mr-2 resize-none outline-none ${appMode === AppMode.LIGHT
                                                ? 'bg-gray-50 border border-gray-200'
                                                : 'bg-gray-700 border border-gray-600 text-white'
                                                }`}
                                            ></textarea>
                                            <textarea placeholder="Right content" className={`flex-1 p-2 ml-2 resize-none outline-none ${appMode === AppMode.LIGHT
                                                ? 'bg-gray-50 border border-gray-200'
                                                : 'bg-gray-700 border border-gray-600 text-white'
                                                }`}
                                            ></textarea>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col h-full">
                                        <input type="text" value={slides[currentSlide]?.title || ''} onChange={(e) => updateSlideContent(currentSlide, 'title', e.target.value)} placeholder="Click to add title" className={`text-xl font-bold mb-4 w-full outline-none ${appMode === AppMode.LIGHT
                                            ? 'bg-white'
                                            : 'bg-gray-800 text-white'
                                            }`}
                                        />
                                        <textarea value={slides[currentSlide]?.content || ''} onChange={(e) => updateSlideContent(currentSlide, 'content', e.target.value)} placeholder="Click to add content" className={`flex-1 p-2 resize-none outline-none ${appMode === AppMode.LIGHT
                                            ? 'bg-gray-50 border border-gray-200'
                                            : 'bg-gray-700 border border-gray-600 text-white'
                                            }`}
                                        ></textarea>
                                    </div>
                                )}
                            </div>
                            <div className="flex justify-between">
                                <div>
                                    <label className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Background color:</label>
                                    <input type="color" value={slides[currentSlide]?.background || '#ffffff'} onChange={(e) => updateSlideContent(currentSlide, 'background', e.target.value)} className="ml-2 cursor-pointer" />
                                </div>
                                <div className="text-sm text-gray-500">Slide {currentSlide + 1} of {slides.length}</div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        };

        return (
            <div className="p-2 w-1/3 h-full">
                <button className={`h-full w-full rounded-lg flex flex-col items-center justify-center ${appMode === AppMode.LIGHT ? light_mode_secondary + ' hover:bg-gray-50' : dark_mode_secondary + ' hover:bg-gray-800'} transition-colors duration-200 shadow-sm hover:shadow-md border ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-700'}`} onClick={handleButtonClick}>
                    <i className={`bi bi-${icon} text-3xl mb-2 ${appMode === AppMode.LIGHT ? 'text-blue-600' : 'text-blue-400'}`}></i>
                    <h1 className="text-lg font-medium">{title}</h1>
                </button>
                {showPopup && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-center z-50">
                        <div ref={showEdRef} className={`${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'} p-6 rounded-lg w-[800px] max-h-[90vh] overflow-auto shadow-xl border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                            <h2 className="text-xl mb-4 font-semibold flex items-center">
                                <i className={`bi bi-${icon} mr-2 ${appMode === AppMode.LIGHT ? 'text-blue-600' : 'text-blue-400'}`}></i> {isCreatingNew ? `Create ${title.replace('New ', '')}` : `Upload ${title}`}
                            </h2>
                            {isCreatingNew
                                ? <>
                                    <div className="mb-4">
                                        <label className={`block mb-2 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Document Name</label>
                                        <input type="text" value={documentName} onChange={(e) => setDocumentName(e.target.value)} className={`w-full px-3 py-2 border rounded-lg ${appMode === AppMode.LIGHT
                                            ? 'border-gray-300 bg-white text-gray-700'
                                            : 'border-gray-600 bg-gray-700 text-gray-300'
                                            } focus:outline-none focus:ring-2 ${appMode === AppMode.LIGHT
                                                ? 'focus:ring-blue-300'
                                                : 'focus:ring-blue-700'
                                            }`} placeholder={`Enter document name (e.g., My${title.replace('New ', '')})`}
                                        />
                                    </div>
                                    {title.includes('Word') && (
                                        <div className="mb-4">
                                            <label className={`block mb-2 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Document Content</label>
                                            <div className={`border rounded-lg ${appMode === AppMode.LIGHT
                                                ? 'border-gray-300 bg-white text-gray-700'
                                                : 'border-gray-600 bg-gray-700 text-gray-300'
                                                }`}>
                                                <div ref={editorRef} className="min-h-[400px]"></div>
                                            </div>
                                        </div>
                                    )}
                                    {title.includes('Excel') && renderExcelEditor()}
                                    {title.includes('PPT') && renderPowerPointEditor()}
                                    <div className="flex justify-end mt-4">
                                        <button className={`px-4 py-2 rounded-lg mr-2 font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT
                                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                                            }`} onClick={handleClosePopup}>Cancel
                                        </button>
                                        <button className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT
                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                            : 'bg-blue-700 text-white hover:bg-blue-800'
                                            }`} onClick={handleCreateDocument}>Create
                                        </button>
                                    </div>
                                </>
                                : <>
                                    <div className={`p-4 border border-dashed rounded-lg mb-4 ${appMode === AppMode.LIGHT
                                        ? 'border-gray-300 bg-gray-50'
                                        : 'border-gray-600 bg-gray-700'
                                        }`}>
                                        <input type="file" accept={acceptedFormats} onChange={handleFileChange} className={`w-full ${appMode === AppMode.LIGHT
                                            ? 'text-gray-700'
                                            : 'text-gray-300'
                                            } file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium ${appMode === AppMode.LIGHT
                                                ? 'file:bg-blue-50 file:text-blue-700'
                                                : 'file:bg-blue-900 file:text-blue-200'
                                            } hover:file:bg-blue-100`}
                                        />
                                    </div>
                                    <div className="flex justify-between mt-4">
                                        <button className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' : 'bg-blue-900 bg-opacity-20 text-blue-400 hover:bg-opacity-30'}`} onClick={() => setIsCreatingNew(true)}><i className="bi bi-plus-circle mr-1.5"></i> Create New Instead</button>
                                        <button className={`px-4 py-2 rounded-lg mr-2 font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`} onClick={handleClosePopup}>Cancel</button>
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const fetchFiles = async (provider, account = null) => {
        setFetchingFiles(true);
        setLoading(true);
        setError(null);
        try {
            let files = [];
            const useAccount = account || activeAccount ||
                (authManager && provider === AuthProvider.GOOGLE
                    ? authManager.getAccounts(AuthProvider.GOOGLE)[0]?.userData
                    : authManager.getAccounts(AuthProvider.MICROSOFT)[0]?.userData);
            if (provider === AuthProvider.GOOGLE && isGoogleLoggedIn === oAuthStatus.LOGGED_IN) {
                files = await fetchGoogleDriveFiles(useAccount, setLoading, setError);
            } else if (provider === AuthProvider.MICROSOFT && isMicrosoftLoggedIn === oAuthStatus.LOGGED_IN) {
                files = await fetchOneDriveFiles(useAccount, setLoading, setError);
            } else {
                throw new Error('User not logged in or invalid provider');
            }
            setFileList(files || []);
            return files;
        } catch (error) {
            setError(`Error fetching files: ${error.message}`);
            setShowErrorPopup(true);
            return [];
        } finally {
            setIsInitialized(true);
            setLoading(false);
            setFetchingFiles(false);
        }
    };

    const downloadFile = async (provider, fileId) => {
        try {
            const account = activeAccount ||
                (authManager && provider === AuthProvider.GOOGLE
                    ? authManager.getAccounts(AuthProvider.GOOGLE)[0]?.userData
                    : authManager.getAccounts(AuthProvider.MICROSOFT)[0]?.userData
                );
            if (provider === AuthProvider.GOOGLE && isGoogleLoggedIn === oAuthStatus.LOGGED_IN) {
                return await downloadGoogleDriveFile(fileId, account, setLoading, setError);
            } else if (provider === AuthProvider.MICROSOFT && isMicrosoftLoggedIn === oAuthStatus.LOGGED_IN) {
                return await downloadOneDriveFile(fileId, account, setLoading, setError);
            } else {
                throw new Error('User not logged in or invalid provider');
            }
        } catch (error) {
            setError(`Error downloading file: ${error.message}`);
            setShowErrorPopup(true);
            return null;
        }
    };

    const createFile = async (provider, fileName, fileContent, mimeType) => {
        try {
            const account = activeAccount ||
                (authManager && provider === AuthProvider.GOOGLE
                    ? authManager.getAccounts(AuthProvider.GOOGLE)[0]?.userData
                    : authManager.getAccounts(AuthProvider.MICROSOFT)[0]?.userData
                );
            if (provider === AuthProvider.GOOGLE && isGoogleLoggedIn === oAuthStatus.LOGGED_IN) {
                return await createGoogleDriveFile(fileName, fileContent, mimeType, account, setLoading, setError);
            } else if (provider === AuthProvider.MICROSOFT && isMicrosoftLoggedIn === oAuthStatus.LOGGED_IN) {
                return await createOneDriveFile(fileName, fileContent, mimeType, account, setLoading, setError);
            } else {
                throw new Error('User not logged in or invalid provider');
            }
        } catch (error) {
            setError(`Error creating file: ${error.message}`);
            setShowErrorPopup(true);
            return null;
        }
    };
    if (error && !showErrorPopup) {
        return (
            <div className={`flex flex-col gap-4 justify-center items-center h-screen ${appMode === AppMode.LIGHT ? light_mode_primary : dark_mode_primary}`}>
                <div className={`${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary} p-8 rounded-lg shadow-lg max-w-md w-full border ${appMode === AppMode.LIGHT ? 'border-red-100' : 'border-red-900'}`}>
                    <div className="flex items-center mb-4">
                        <div className="flex-shrink-0 bg-red-100 rounded-full p-2 mr-3">
                            <i className="bi bi-exclamation-triangle-fill text-red-600 text-xl"></i>
                        </div>
                        <h2 className="text-xl font-semibold">Error</h2>
                    </div>
                    <p className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>{error}</p>
                </div>
                <div className="flex gap-3">
                    <button onClick={() => setError(null)} className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-gray-200 text-gray-800 hover:bg-gray-300' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}>Dismiss</button>
                    <button onClick={() => window.location.reload()} className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 font-medium shadow-sm hover:shadow">Retry</button>
                </div>
            </div>
        );
    }

    if (!isInitialized || loading) {
        return <LoadingScreen appMode={appMode} />;
    }

    if (!user && (isGoogleLoggedIn === oAuthStatus.NOT_LOGGED_IN || isGoogleLoggedIn === oAuthStatus.NOT_DETERMINED) &&
        (isMicrosoftLoggedIn === oAuthStatus.NOT_LOGGED_IN || isMicrosoftLoggedIn === oAuthStatus.NOT_DETERMINED)) {
        return <NoAuthScreen appMode={appMode} setActiveTab={setActiveTab} />;
    }

    return (
        <div className={`flex-col ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_primary} h-screen overflow-y-auto`}>
            <div className="p-2 h-1/3">
                <div className={`h-full ${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary} rounded-lg shadow-sm border ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-800'} transition-all duration-300`}>
                    <ProfileHeader appMode={appMode} title={'Explore Files'} subtitle={false} setAuthStatus={setAuthStatus} toggleAppMode={toggleAppMode} />
                    <div className="px-3.5 py-5 flex justify-between">
                        <div className="w-1/3">
                            <div className={`h-12 flex border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} rounded-lg overflow-hidden shadow-sm focus-within:shadow focus-within:border-blue-400 transition-all duration-200`}>
                                <input className={`px-3.5 w-5/6 focus:outline-none bg-transparent ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'} placeholder-gray-400`} placeholder="Search files" value={searchQuery} onChange={handleSearch} />
                                <button className={`w-1/6 flex items-center justify-center ${appMode === AppMode.LIGHT ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-200'} transition-colors duration-200`} onClick={() => handleSearch({ target: { value: searchQuery } })}>
                                    {isSearching
                                        ? <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                                        : <i className="bi bi-search"></i>
                                    }
                                </button>
                            </div>
                        </div>
                        <FeatureButton title={'Sort By: Popular'} icon={'bi bi-filter'} />
                    </div>
                </div>
            </div>
            <h1 className='px-3 py-2 font-semibold text-2xl'>New</h1>
            <div className="p-2 h-1/4 flex">
                <NewDocButton title={'New Word Doc'} icon={'file-word'} acceptedFormats=".doc,.docx" />
                <NewDocButton title={'New Excel Sheet'} icon={'file-earmark-spreadsheet'} acceptedFormats=".xls,.xlsx" />
                <NewDocButton title={'New PPT'} icon={'filetype-ppt'} acceptedFormats=".ppt,.pptx" />
            </div>
            <h1 className='px-3 py-2 font-semibold text-2xl'>Recent Files</h1>
            <div className="p-2 flex h-3/4">
                <div className="pr-2 w-2/3">
                    <div className={`h-full w-full ${appMode === AppMode.LIGHT ? 'bg-white text-[#768396]' : dark_mode_secondary + ' text-white'} rounded-lg shadow-sm border ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-800'} transition-all duration-300`}>
                        {loading
                            ? <div className="flex justify-center items-center h-full">
                                <div className="flex flex-col items-center">
                                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mb-2"></div>
                                    <span className="text-sm font-medium">Loading files...</span>
                                </div>
                            </div>
                            : fileList.length > 0
                                ? <Table appMode={appMode} files={fileList} selectedProvider={selectedProvider} downloadFile={downloadFile} onPreviewFile={handlePreviewFile} />
                                : <div className="flex flex-col justify-center items-center h-full">
                                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${appMode === AppMode.LIGHT ? 'bg-gray-50' : 'bg-gray-800'}`}>
                                        <i className="bi bi-folder-x text-4xl text-gray-400"></i>
                                    </div>
                                    <span className={`text-base font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>No files found</span>
                                    {selectedProvider
                                        ? <span className="text-sm text-gray-500 mt-1">Try uploading a file using the options above</span>
                                        : <span className="text-sm text-gray-500 mt-1">Select a storage provider to view files</span>
                                    }
                                    {selectedProvider &&
                                        <button onClick={() => fetchFiles(selectedProvider)} className={`mt-4 px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' : 'bg-blue-900 bg-opacity-20 text-blue-400 hover:bg-opacity-30'}`}>
                                            <i className="bi bi-arrow-clockwise mr-1"></i> Refresh
                                        </button>
                                    }
                                </div>
                        }
                    </div>
                </div>
                <div className="pl-2 w-1/3">
                    <div className={`h-full p-4 ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_secondary} rounded-lg shadow-sm border ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-800'} transition-all duration-300`}>
                        <h2 className="text-lg font-semibold mb-3 flex items-center">Connected Accounts</h2>
                        <div className="space-y-3 mb-4">
                            {accounts.map((account, index) => {
                                const userData = account.userData || {};
                                const isActive = activeAccount && (activeAccount.id === account.id);
                                return (
                                    <div key={account.id || index} className={`p-3 rounded-lg flex items-center justify-between ${isActive
                                        ? appMode === AppMode.LIGHT
                                            ? 'bg-blue-50 border border-blue-100'
                                            : 'bg-blue-900 bg-opacity-20 border border-blue-800'
                                        : appMode === AppMode.LIGHT
                                            ? 'bg-gray-50 border border-gray-100 hover:bg-gray-100'
                                            : 'bg-gray-800 border border-gray-700 hover:bg-gray-700'
                                        } cursor-pointer transition-all duration-200 shadow-sm hover:shadow`}
                                        onClick={() => handleAccountSelect(account)}
                                    >
                                        <div className="flex items-center">
                                            {account.provider === AuthProvider.MICROSOFT
                                                ? <i className="bi bi-microsoft text-blue-500 mr-2 text-lg"></i>
                                                : <i className="bi bi-google text-red-500 mr-2 text-lg"></i>
                                            }
                                            <div>
                                                <div className="font-medium">{userData.name || userData.username || userData.email || 'Unknown'}</div>
                                                <div className="text-xs text-gray-500">{userData.email || userData.username || 'No email'}</div>
                                            </div>
                                        </div>
                                        {isActive && <div className="text-blue-500">
                                            <i className="bi bi-check-circle-fill"></i>
                                        </div>
                                        }
                                    </div>
                                );
                            })}
                        </div>
                        {subscriptionInfo && (
                            <div className={`mt-6 p-4 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-gray-50 border border-gray-100' : 'bg-gray-800 border border-gray-700'} shadow-sm`}>
                                <div className="flex justify-between items-center">
                                    <div>
                                        <div className={`text-sm font-semibold flex items-center ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>
                                            <i className={`bi bi-stars mr-1.5 ${subscriptionInfo.tier === PaymentTiers.FREE ? 'text-gray-400' : 'text-yellow-500'}`}></i> {subscriptionInfo.name} Plan
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">{subscriptionInfo.accountsUsed} of {subscriptionInfo.accountLimit === 15 ? 15 : subscriptionInfo.accountLimit} accounts used</div>
                                        <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                                            <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${(subscriptionInfo.accountsUsed / subscriptionInfo.accountLimit) * 100}%` }}></div>
                                        </div>
                                    </div>
                                    {!subscriptionInfo.canAddMore && subscriptionInfo.tier !== PaymentTiers.ENTERPRISE && <button onClick={handleUpgradeRedirect} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-700 hover:bg-purple-800'} text-white shadow-sm hover:shadow`}>Upgrade</button>}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showAccountSelector && <AccountSelector appMode={appMode} accounts={accounts} onClose={() => setShowAccountSelector(false)} onSelectAccount={handleAccountSelect} onRemoveAccount={handleRemoveAccount} activeAccount={activeAccount} subscriptionInfo={subscriptionInfo} onUpgrade={handleUpgradeRedirect} />}
            {showErrorPopup && error && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-center z-50">
                    <div className={`${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_secondary} p-6 rounded-lg w-96 shadow-xl border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                        <div className="flex items-center mb-4">
                            <div className="flex-shrink-0 bg-red-100 rounded-full p-2 mr-3">
                                <i className="bi bi-exclamation-triangle-fill text-red-600 text-xl"></i>
                            </div>
                            <h2 className="text-xl font-semibold">Error</h2>
                        </div>
                        <p className={`mb-4 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>{error}</p>
                        <div className="flex justify-end">
                            <button className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-red-600 hover:bg-red-700' : 'bg-red-700 hover:bg-red-800'} text-white shadow-sm hover:shadow`} onClick={() => { setShowErrorPopup(false); setError(null); }}>Close</button>
                        </div>
                    </div>
                </div>
            )}
            {showUpgradePrompt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className={`w-96 rounded-lg shadow-xl ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-gray-800 border border-gray-700'} transition-all duration-300`}>
                        <div className="p-4 border-b flex justify-between items-center">
                            <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Account Limit Reached</h2>
                            <button onClick={() => setShowUpgradePrompt(false)} className={`p-1.5 rounded-full ${appMode === AppMode.LIGHT ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700' : 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'} transition-colors duration-200`}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="text-center mb-4">
                                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${appMode === AppMode.LIGHT ? 'bg-yellow-100 text-yellow-500' : 'bg-yellow-900 bg-opacity-30 text-yellow-400'} mb-4`}>
                                    <i className="bi bi-exclamation-triangle-fill text-2xl"></i>
                                </div>
                                <h3 className={`text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Subscription Limit Reached</h3>
                                <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>You've reached the maximum number of accounts for your current {subscriptionInfo?.name} plan.</p>
                            </div>
                            <div className="flex justify-center space-x-3">
                                <button onClick={() => setShowUpgradePrompt(false)} className={`px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}>Cancel</button>
                                <button onClick={handleUpgradeRedirect} className={`px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-700 hover:bg-purple-800'} text-white shadow-sm hover:shadow`}>Upgrade Now</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <FilePreviewPopup appMode={appMode} file={previewFile} isOpen={isPreviewOpen} onClose={handleClosePreview} accessToken={accessToken} />
        </div>
    );
}

function Table({ appMode, files, selectedProvider, downloadFile, onPreviewFile }) {
    const handleDownload = async (fileId) => {
        try {
            const fileContent = await downloadFile(selectedProvider, fileId);
            const blob = new Blob([fileContent], { type: 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = files.find(file => file.id === fileId)?.name || 'download';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
        catch (error) { }
    };

    return (
        <section className={`w-full h-full overflow-y-auto md:rounded-lg ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_secondary}`}>
            <table className="w-full h-full divide-y divide-gray-100">
                <thead className={`sticky top-0 z-10 border-b ${appMode === AppMode.LIGHT ? 'border-gray-100 bg-white' : 'border-gray-700 bg-gray-800'}`}>
                    <tr className="z-10">
                        <th scope="col" className="p-3 h-12 w-1/3 text-sm font-medium">
                            <div className={`px-3 flex justify-between border-r ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                                <h1>File name</h1>
                                <button className={`bi bi-filter ${appMode === AppMode.LIGHT ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-200'} transition-colors duration-200`}></button>
                            </div>
                        </th>
                        <th scope="col" className="p-3 h-12 w-1/3 text-sm font-medium">
                            <div className={`px-3 flex justify-between border-r ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                                <h1>File size</h1>
                                <button className={`bi bi-filter ${appMode === AppMode.LIGHT ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-200'} transition-colors duration-200`}></button>
                            </div>
                        </th>
                        <th scope="col" className="p-3.5 h-12 w-1/3 text-sm font-medium">
                            <div className="flex justify-between text-start">
                                <h1>Date modified</h1>
                                <button className={`bi bi-filter ${appMode === AppMode.LIGHT ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-200'} transition-colors duration-200`}></button>
                            </div>
                        </th>
                        {selectedProvider && <th scope="col" className="p-3.5 h-12 w-1/3 text-sm font-medium">
                            <div className="flex justify-center"><h1>Actions</h1></div>
                        </th>
                        }
                    </tr>
                </thead>
                <tbody className={`${appMode === AppMode.LIGHT ? 'divide-y divide-gray-100' : 'divide-y divide-gray-800'}`}>
                    {files.length === 0
                        ? <tr>
                            <td colSpan={selectedProvider ? 4 : 3} className={`px-4 py-8 text-center ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>{selectedProvider ? "No files found" : "Select a storage provider to view files"}</td>
                        </tr>
                        : files.map((file, index) => (
                            <tr key={index} className={`text-start ${appMode === AppMode.LIGHT ? 'hover:bg-gray-50' : 'hover:bg-gray-800'} transition-colors duration-200`}>
                                <td className="px-4 py-4 text-sm font-medium whitespace-nowrap overflow-hidden truncate">
                                    <div className="inline-flex items-center gap-x-3">
                                        <div className="flex items-center gap-x-2">
                                            <div className={`flex items-center justify-center w-8 h-8 ${appMode === AppMode.LIGHT ? 'text-blue-500 bg-blue-100' : 'text-blue-400 bg-blue-900 bg-opacity-30'} rounded-full`}>
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                                </svg>
                                            </div>
                                            <button className={`font-medium ${appMode === AppMode.LIGHT ? 'hover:text-blue-600' : 'hover:text-blue-400'} transition-colors duration-200`} onClick={() => onPreviewFile(file)}>{file.name}</button>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-12 py-4 text-sm font-normal whitespace-nowrap">{typeof file.size === 'number' ? `${Math.round(file.size / 1024)} KB` : file.size || 'Unknown'}</td>
                                <td className="px-4 py-4 text-sm whitespace-nowrap">
                                    {file.modifiedTime
                                        ? new Date(file.modifiedTime).toLocaleDateString()
                                        : file.lastModifiedDateTime
                                            ? new Date(file.lastModifiedDateTime).toLocaleDateString()
                                            : 'Unknown'
                                    }
                                </td>
                                {selectedProvider && (
                                    <td className="px-4 py-4 text-sm whitespace-nowrap">
                                        <div className="flex gap-3 justify-center">
                                            <button onClick={() => onPreviewFile(file)} className={`px-3 py-1.5 rounded-lg flex items-center ${appMode === AppMode.LIGHT ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' : 'text-blue-400 bg-blue-900 bg-opacity-20 hover:bg-opacity-30'} transition-colors duration-200`}>
                                                <i className="bi bi-eye mr-1.5"></i> Preview
                                            </button>
                                            <button onClick={() => handleDownload(file.id)} className={`px-3 py-1.5 rounded-lg flex items-center ${appMode === AppMode.LIGHT ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-green-400 bg-green-900 bg-opacity-20 hover:bg-opacity-30'} transition-colors duration-200`}>
                                                <i className="bi bi-download mr-1.5"></i> Download
                                            </button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                </tbody>
            </table>
        </section>
    );
}
